import api from '../../../services/api'
import type { BruktbilFilters } from '../useBruktbilFilters'
import { buildBruktbilQs } from '../qs'

type BaseParams = Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> & { selgerPostnummer?: string | null; kjoperPostnummer?: string | null }

function baseParams(f: BaseParams) {
  return {
    from_date: f.from,
    to_date: f.to,
    klasse: f.klasse,
    varebil: f.varebil,
    omregistrering: f.omregistrering || undefined,
    ...(f.selgerPostnummer ? { selger_postnummer: f.selgerPostnummer.split(',') } : {}),
    ...(f.kjoperPostnummer ? { kjoper_postnummer: f.kjoperPostnummer.split(',') } : {}),
  }
}

export interface OrgMerkeRow { merke: string; count: number; share_pct: number; topp_modell: string | null }
export interface OrgMerkerResponse { org_nr: string; rows: OrgMerkeRow[]; total: number }

export interface OrgModellRow { merke: string; modell: string; count: number; share_pct: number }
// total counts only the rows that carry a model, so share_pct sums to 100.
export interface OrgModellerResponse { org_nr: string; rows: OrgModellRow[]; total: number }

export interface OrgKlasseRow { klasse: string; count: number; share_pct: number }
export interface OrgKlasserResponse { org_nr: string; rows: OrgKlasseRow[]; total: number }

export interface OrgAlderYearBucket { aar: number; antall: number }
export interface OrgAlderStats {
  snittalder: number | null
  medianalder: number | null
  min_alder: number | null
  max_alder: number | null
  p25: number | null
  p75: number | null
}
export interface OrgAlderSeriesBucket { aar: number; antall: number; andel_pct: number }
export interface OrgAlderSeries {
  merke: string
  buckets: OrgAlderSeriesBucket[]
  over_25: number
  over_25_andel_pct: number
  total: number
  // Grid under the chart (#586 AC4) — exact over the real ages, so a brand
  // with many 25+ cars reads higher here than the clamped chart looks.
  snittalder: number | null
  medianalder: number | null
}
export interface OrgAldersprofilResponse {
  org_nr: string
  buckets: OrgAlderYearBucket[]
  over_25: number
  stats: OrgAlderStats
  total: number
  series: OrgAlderSeries[]
}

export interface OrgKjoperAlderYearBucket { alder: number; antall: number }
export interface OrgKjoperAlderSeriesBucket { alder: number; antall: number; andel_pct: number }
export interface OrgKjoperAlderSeries {
  merke: string
  buckets: OrgKjoperAlderSeriesBucket[]
  over_75: number
  over_75_andel_pct: number
  total: number
  // Grid under the chart (#586 AC3). Median is whole years; mean is not.
  snittalder: number | null
  medianalder: number | null
}
export interface OrgKjoperAlderResponse {
  org_nr: string
  buckets: OrgKjoperAlderYearBucket[]
  over_75: number
  snittalder: number | null
  medianalder: number | null
  total: number
  series: OrgKjoperAlderSeries[]
  // False when the org has no private buyers at all — the tab is then hidden
  // rather than rendered empty (#485 AC2).
  has_private_buyers: boolean
}

export interface OrgDrivstoffRow { drivstoff: string; count: number; share_pct: number }
// One matrix column; merke is "Alle" for the whole-org column (#586 AC5).
export interface OrgDrivstoffSerie { merke: string; rows: OrgDrivstoffRow[]; total: number }
export interface OrgDrivstoffResponse {
  org_nr: string; rows: OrgDrivstoffRow[]; total: number
  series: OrgDrivstoffSerie[]
}

type Side = 'kjopere' | 'selgere'

export const fetchOrgMerker = (side: Side, orgNr: string, f: BaseParams) =>
  api.get<OrgMerkerResponse>(`/bruktbil/${side}/${orgNr}/merker`, {
    params: baseParams(f),
    paramsSerializer: { indexes: null },
  })

export const fetchOrgModeller = (side: Side, orgNr: string, f: BaseParams) =>
  api.get<OrgModellerResponse>(`/bruktbil/${side}/${orgNr}/modeller`, {
    params: baseParams(f),
    paramsSerializer: { indexes: null },
  })

export const fetchOrgKlasser = (side: Side, orgNr: string, f: BaseParams) =>
  api.get<OrgKlasserResponse>(`/bruktbil/${side}/${orgNr}/klasser`, {
    params: baseParams(f),
    paramsSerializer: { indexes: null },
  })

export const fetchOrgAldersprofil = (side: Side, orgNr: string, f: BaseParams, merker: string[] = []) =>
  api.get<OrgAldersprofilResponse>(
    `/bruktbil/${side}/${orgNr}/aldersprofil?${buildBruktbilQs(baseParams(f), merker)}`,
  )

export const fetchOrgKjoperalder = (side: Side, orgNr: string, f: BaseParams, merker: string[] = []) =>
  api.get<OrgKjoperAlderResponse>(
    `/bruktbil/${side}/${orgNr}/kjoperalder?${buildBruktbilQs(baseParams(f), merker)}`,
  )

export const fetchOrgDrivstoff = (side: Side, orgNr: string, f: BaseParams, merker: string[] = []) =>
  api.get<OrgDrivstoffResponse>(
    `/bruktbil/${side}/${orgNr}/drivstoff?${buildBruktbilQs(baseParams(f), merker)}`,
  )
